import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import * as workLogService from "@/services/workLog.service";
import { notifyError, notifySuccess } from "@/lib/notification";

export const workLogKeys = {
  all: ["work-logs"] as const,
  lists: (params?: any) => [...workLogKeys.all, "list", params] as const,
  detail: (id: string) => [...workLogKeys.all, "detail", id] as const,
};

export const useWorkLogsQuery = (params?: workLogService.WorkLogParamsType) =>
  useQuery({
    queryKey: workLogKeys.lists(params),
    queryFn: () => workLogService.fetchWorkLogs(params),
    placeholderData: (prev) => prev,
  });

export const useWorkLogByIdQuery = (id: string) =>
  useQuery({
    queryKey: workLogKeys.detail(id),
    queryFn: () => workLogService.getWorkLogById(id),
    enabled: !!id,
  });

export const useCreateWorkLogMutation = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: workLogService.WorkLogCreateType) => workLogService.createWorkLog(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: workLogKeys.all });
      notifySuccess("Work log created successfully!");
    },
    onError: (error: any) => {
      notifyError(error?.response?.data?.message || "Failed to create work log");
    },
  });
};

export const useUpdateWorkLogMutation = (logId: string) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: workLogService.WorkLogUpdateType) => workLogService.updateWorkLog(logId, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: workLogKeys.all });
      queryClient.invalidateQueries({ queryKey: workLogKeys.detail(logId) });
      notifySuccess("Work log updated successfully!");
    },
    onError: (error: any) => {
      notifyError(error?.response?.data?.message || "Failed to update work log");
    },
  });
};

export const useDeleteWorkLogMutation = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (logId: string) => workLogService.deleteWorkLog(logId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: workLogKeys.all });
      notifySuccess("Work log deleted successfully.");
    },
    onError: (error: any) => {
      notifyError(error?.response?.data?.message || "Failed to delete work log");
    },
  });
};
